import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Box, Button, ButtonGroup, Flex, Textarea, useColorModeValue } from '@chakra-ui/react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Upload an image and return its public URL; enables the toolbar Image button. */
  onUploadImage?: (file: File) => Promise<string>;
}

/**
 * Chakra-styled Tiptap WYSIWYG with a raw-HTML source toggle (FR-062). The editor
 * and the source Textarea bind to the same HTML string, so toggling round-trips.
 */
export function RichTextEditor({ value, onChange, onUploadImage }: RichTextEditorProps) {
  const [sourceMode, setSourceMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  const editor = useEditor({
    extensions: [StarterKit, Image, Link.configure({ openOnClick: false })],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. switching language tabs) into the editor.
  useEffect(() => {
    if (editor && !sourceMode && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor, sourceMode]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onUploadImage || !editor) return;
    const url = await onUploadImage(file);
    editor.chain().focus().setImage({ src: url }).run();
  };

  if (!editor) return null;

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="12px" overflow="hidden">
      <Flex p="8px" gap="8px" borderBottomWidth="1px" borderColor={borderColor} flexWrap="wrap" align="center">
        <Toolbar
          editor={editor}
          disabled={sourceMode}
          hasUpload={Boolean(onUploadImage)}
          onImageClick={() => fileInputRef.current?.click()}
        />
        <Button size="xs" ml="auto" variant={sourceMode ? 'solid' : 'outline'} onClick={() => setSourceMode((s) => !s)}>
          {sourceMode ? 'Editor' : 'HTML'}
        </Button>
      </Flex>

      {sourceMode ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fontFamily="mono"
          fontSize="sm"
          minH="280px"
          border="none"
          borderRadius="0"
          _focusVisible={{ boxShadow: 'none' }}
        />
      ) : (
        <Box
          p="12px"
          minH="280px"
          sx={{
            '& .ProseMirror': { minHeight: '256px', outline: 'none' },
            '& .ProseMirror p': { mb: '8px' },
            '& .ProseMirror h2': { fontSize: 'xl', fontWeight: 'bold', mb: '8px' },
            '& .ProseMirror ul, & .ProseMirror ol': { pl: '20px', mb: '8px' },
            '& .ProseMirror img': { maxWidth: '100%', borderRadius: '8px' },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
    </Box>
  );
}

function Toolbar({
  editor,
  disabled,
  hasUpload,
  onImageClick,
}: {
  editor: Editor;
  disabled: boolean;
  hasUpload: boolean;
  onImageClick: () => void;
}) {
  const variant = (active: boolean) => (active ? 'solid' : 'outline');
  return (
    <ButtonGroup spacing="4px" flexWrap="wrap">
      <Button size="xs" fontWeight="bold" variant={variant(editor.isActive('bold'))} isDisabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}>B</Button>
      <Button size="xs" fontStyle="italic" variant={variant(editor.isActive('italic'))} isDisabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}>I</Button>
      <Button size="xs" variant={variant(editor.isActive('heading', { level: 2 }))} isDisabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Button>
      <Button size="xs" variant={variant(editor.isActive('bulletList'))} isDisabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</Button>
      <Button size="xs" variant={variant(editor.isActive('orderedList'))} isDisabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</Button>
      <Button
        size="xs"
        variant={variant(editor.isActive('link'))}
        isDisabled={disabled}
        onClick={() => {
          const url = window.prompt('Link URL');
          if (url === null) return;
          if (url === '') editor.chain().focus().unsetLink().run();
          else editor.chain().focus().setLink({ href: url }).run();
        }}
      >
        Link
      </Button>
      {hasUpload && (
        <Button size="xs" variant="outline" isDisabled={disabled} onClick={onImageClick}>
          Image
        </Button>
      )}
    </ButtonGroup>
  );
}
